/**
 * Passport · Section D — What it really costs
 *
 * Pulls the asking price through:
 *   - Stamp duty
 *   - Help-to-Buy refund
 *   - First Home Scheme equity
 *   - LPT (current + projected post-revaluation)
 *   - Real mortgage capacity vs Central Bank rules
 *   - Cash-at-close + monthly carrying cost
 *
 * When the buyer hasn't filled their profile, we render with assumed defaults
 * and show a "Personalise" badge inviting the buyer to add income/deposit.
 */
import React from 'react';
import { Wallet, TrendingDown, Building, Calendar, AlertTriangle, Info } from 'lucide-react';
import { Badge } from '../ui/badge';

const makeFmt = (section = {}) => {
  const locale = section.locale || 'en-IE';
  const currency = section.currency || 'EUR';
  return (n) =>
    n == null ? '—'
      : new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
};
const sym = (section = {}) => section.currency_symbol || '€';

const Bar = ({ items, fmt }) => {
  const total = items.reduce((s, it) => s + Math.max(0, it.value), 0) || 1;
  return (
    <div className="flex w-full h-2 rounded overflow-hidden bg-stone-100">
      {items.map((it) => (
        <div
          key={it.label} title={`${it.label}: ${fmt(it.value)}`}
          style={{ width: `${(Math.max(0, it.value) / total) * 100}%` }}
          className={it.color}
        />
      ))}
    </div>
  );
};

const RealCostSection = ({ section }) => {
  const fmtEur = makeFmt(section);
  const $ = sym(section);
  const isUK = section.country_label === 'UK';
  const stampLabel = isUK ? 'Stamp Duty (SDLT)' : 'Stamp duty';
  const lptLabel = isUK ? 'Council Tax (Band D)' : 'LPT / year';
  const lptCardTitle = isUK ? 'Council Tax / year' : 'LPT / year';
  const htbLabel = isUK ? 'UK ownership schemes' : 'Help-to-Buy refund';
  const fhsLabel = isUK ? 'First Homes Scheme (UK)' : 'First Home Scheme equity';
  const d = section.details || {};
  const cap = d.mortgage || {};
  const fhs = d.fhs || {};
  const lpt = d.lpt || {};

  const cashItems = [
    { label: 'Deposit',    value: d.mortgage?.deposit_eur || 0, color: 'bg-purple-600' },
    { label: stampLabel,   value: d.stamp_duty_eur || 0,        color: 'bg-amber-500' },
    { label: 'Legal fees', value: d.legal_fees_est_eur || 0,    color: 'bg-stone-400' },
    { label: 'HTB refund', value: -(d.htb_refund_eur || 0),     color: 'bg-emerald-500' },
  ];
  const gap = cap.gap_eur || 0;

  return (
    <div className="space-y-4 pt-2">
      {/* Personalise hint */}
      {!d.profile_supplied && (
        <div
          className="flex items-start gap-2 rounded-md border border-purple-200 bg-purple-50/50 p-2 text-xs"
          data-testid="passport-cost-personalise"
        >
          <Info className="h-4 w-4 text-purple-700 flex-shrink-0 mt-0.5" />
          <div>
            Showing the <strong>generic FTB example</strong> ({$}{cap.income_eur?.toLocaleString()} income · {$}{cap.deposit_eur?.toLocaleString()} deposit · FTB · 30 yr term).
            Personalise this in your <strong>Buyer Profile</strong> for accurate figures.
          </div>
        </div>
      )}

      {/* Top row — cash at close + monthly + LPT */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-4" data-testid="passport-cash-card">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="h-4 w-4 text-purple-700" />
            <span className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">Cash at close</span>
          </div>
          <div className="text-2xl font-bold text-stone-900">{fmtEur(d.cash_at_close_eur)}</div>
          <div className="text-[11px] text-stone-500 mt-1">Deposit + {stampLabel} + Legal {isUK ? '' : '− HTB'}</div>
          <div className="mt-2">
            <Bar items={cashItems} fmt={fmtEur} />
          </div>
        </div>

        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="h-4 w-4 text-stone-500" />
            <span className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">Mortgage / month</span>
          </div>
          <div className="text-2xl font-bold text-stone-900">{fmtEur(cap.est_monthly_eur)}</div>
          <div className="text-[11px] text-stone-500 mt-1">
            @ {cap.stress_rate_pct?.toFixed(1)}% test rate · 30 yr term
          </div>
        </div>

        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-1">
            <Building className="h-4 w-4 text-stone-500" />
            <span className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">{lptCardTitle}</span>
          </div>
          <div className="text-2xl font-bold text-stone-900">{fmtEur(lpt.current_annual_eur)}</div>
          <div className="text-[11px] text-stone-500 mt-1">
            {isUK ? 'Projected next year' : 'Projected post-revaluation'}: <strong>{fmtEur(lpt.projected_annual_eur)}</strong>
          </div>
        </div>
      </div>

      {/* Mortgage capacity panel */}
      <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-mortgage-card">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-semibold text-stone-900">{isUK ? 'Mortgage capacity (FCA / FPC rules)' : 'Mortgage capacity (Central Bank rules)'}</span>
          {gap > 0
            ? <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px] py-0">Gap {fmtEur(gap)}</Badge>
            : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] py-0">In reach</Badge>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase text-stone-500 font-semibold">You need</div>
            <div className="font-bold text-stone-900">{fmtEur(cap.needed_borrow_eur)}</div>
            <div className="text-[10px] text-stone-500">price − deposit</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-stone-500 font-semibold">Max borrow</div>
            <div className="font-bold text-stone-900">{fmtEur(cap.max_borrow_eur)}</div>
            <div className="text-[10px] text-stone-500">
              {cap.lti_multiple}× LTI · {cap.ltv_max_pct}% LTV
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-stone-500 font-semibold">Gap</div>
            <div className={`font-bold ${gap > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
              {gap > 0 ? fmtEur(gap) : 'None'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-stone-500 font-semibold">Stress rate</div>
            <div className="font-bold text-stone-900">{cap.stress_rate_pct?.toFixed(1)}%</div>
          </div>
        </div>
      </div>

      {/* HTB + FHS row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="passport-htb-card">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="h-4 w-4 text-emerald-700" />
            <span className="font-semibold text-stone-900">{htbLabel}</span>
          </div>
          {isUK ? (
            <div className="text-sm text-stone-600">
              UK Help-to-Buy <strong>closed October 2022</strong>. The active scheme is the <strong>Lifetime ISA</strong> (25% government bonus on FTB deposit savings up to £4k/yr).
            </div>
          ) : d.htb_refund_eur > 0 ? (
            <>
              <div className="text-xl font-bold text-emerald-700">{fmtEur(d.htb_refund_eur)}</div>
              <div className="text-[11px] text-stone-600">Refund applied at close (FTB + new build).</div>
            </>
          ) : (
            <div className="text-sm text-stone-600">
              Not eligible — requires <strong>new-build</strong> + first-time buyer.
            </div>
          )}
        </div>

        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="passport-fhs-card">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="h-4 w-4 text-emerald-700" />
            <span className="font-semibold text-stone-900">{fhsLabel}</span>
          </div>
          {isUK ? (
            <div className="text-sm text-stone-600">{fhs.reason}</div>
          ) : fhs.eligible ? (
            <>
              <div className="text-xl font-bold text-emerald-700">
                {fmtEur(fhs.equity_eur)} <span className="text-xs text-stone-500 font-normal">({fhs.max_pct}% equity stake)</span>
              </div>
              <div className="text-[11px] text-stone-600">State takes equity in lieu of cash. No repayment for 5 years.</div>
            </>
          ) : (
            <div className="text-sm text-stone-600">
              Not eligible — requires new-build + FTB and price ≤ <strong>{fmtEur(fhs.cap_eur)}</strong> for this county.
            </div>
          )}
        </div>
      </div>

      {/* Gap nudge */}
      {gap > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 flex items-start gap-2 text-sm" data-testid="passport-gap-nudge">
          <AlertTriangle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-stone-800">
            You&apos;d be <strong>{fmtEur(gap)}</strong> short on financing this house under current {isUK ? 'FCA' : 'Central Bank'} rules.
            Options: increase deposit, second income on the application, or look at homes up to {fmtEur((cap.max_borrow_eur || 0) + (cap.deposit_eur || 0))}.
          </div>
        </div>
      )}

      <div className="text-[10px] text-stone-400">
        {d.rules_label || 'Estimates only. Verify with a broker. Sources: Revenue (Stamp Duty, HTB, LPT), gov.ie (FHS), Central Bank macroprudential rules, CSO RPPI.'}
      </div>
    </div>
  );
};

export default RealCostSection;
